import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import Flag from "react-world-flags";

import { COUNTRIES, countryName } from "@/modules/dashboard/cards/constants/countries.constants";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import { PaymentDirection, type ProviderRailRoute } from "../../types/paymentFees.types";

export function DirectionTag({ direction, className }: { direction: PaymentDirection; className?: string }) {
  const labels = useFeeLabels();
  const isPayin = direction === PaymentDirection.PAYIN;
  const Icon = isPayin ? ArrowDownLeft : ArrowUpRight;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[11px] font-medium",
        isPayin ? "text-success" : "text-blue-700 dark:text-blue-300",
        className,
      )}
    >
      <Icon className="size-3" aria-hidden="true" />
      {labels.direction[direction]}
    </span>
  );
}

export function CountryFlag({ iso3, className }: { iso3: string; className?: string }) {
  const iso2 = COUNTRIES.find((country) => country.iso3 === iso3)?.iso2;
  if (!iso2) return null;
  return (
    <Flag
      code={iso2}
      aria-hidden="true"
      className={cn("h-3.5 w-5 shrink-0 rounded-[2px] object-cover ring-1 ring-border", className)}
    />
  );
}

type CorridorRoute = Pick<ProviderRailRoute, "country" | "rail" | "currency" | "direction">;

/**
 * `stacked`: rail and direction on top, country · currency underneath (table rows). `inline`: one line
 * (dialogs and lists).
 */
export function CorridorIdentity({
  route,
  layout = "inline",
  note,
  className,
}: {
  route: CorridorRoute;
  layout?: "stacked" | "inline";
  /** An extra muted fact for the second line, e.g. that Coloca absorbs the cost. */
  note?: string;
  className?: string;
}) {
  const place = `${countryName(route.country)} · ${route.currency}`;

  if (layout === "inline") {
    return (
      <span className={cn("inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5", className)}>
        <CountryFlag iso3={route.country} />
        <span className="text-sm font-medium">{formatRail(route.rail)}</span>
        <span className="text-xs text-muted-foreground">{place}</span>
        <DirectionTag direction={route.direction} />
      </span>
    );
  }

  return (
    <span className={cn("flex items-start gap-2.5", className)}>
      <CountryFlag iso3={route.country} className="mt-0.5" />
      <span className="flex min-w-0 flex-col">
        <span className="flex flex-wrap items-center gap-x-2">
          <span className="text-sm font-medium">{formatRail(route.rail)}</span>
          <DirectionTag direction={route.direction} />
        </span>
        <span className="text-xs text-muted-foreground">
          {place}
          {note ? ` · ${note}` : ""}
        </span>
      </span>
    </span>
  );
}
